import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, formatError } from "@/lib/api";
import { UserCircle, Camera, Trash, Spinner } from "@phosphor-icons/react";

/**
 * PhotoUploader — round avatar-style uploader.
 *
 * Props:
 *  value      string | null  — current photo_url (e.g. "/api/files/xxx.png") OR null
 *  onChange(url|null)        — called with the new URL or null when cleared
 *  category   string         — backend label: "employee" | "machine" | "misc"
 *  size       number         — pixel size of the square preview (default 96)
 *  disabled   bool           — readonly mode
 *  testId     string         — data-testid prefix
 */
export default function PhotoUploader({
  value, onChange, category = "misc", size = 96, disabled = false, testId = "photo",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  function fullSrc(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    // backend serves relative /api/files/... → prepend backend URL
    const base = API_BASE.replace(/\/api$/, "");
    return `${base}${url}`;
  }

  async function pick() {
    if (disabled || uploading) return;
    fileRef.current?.click();
  }

  async function onFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";  // reset so re-selecting same file works
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image is over 5 MB — please choose a smaller one");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/upload?category=${encodeURIComponent(category)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  }

  function clearPhoto() {
    onChange?.(null);
  }

  const src = fullSrc(value);
  const dim = { width: size, height: size };

  return (
    <div className="flex items-center gap-4" data-testid={`${testId}-uploader`}>
      <div
        className="relative border border-[#e4e4e7] bg-[#f4f4f5] overflow-hidden flex items-center justify-center"
        style={dim}
      >
        {src ? (
          <img
            src={src}
            alt="upload preview"
            className="object-cover w-full h-full"
            data-testid={`${testId}-preview`}
          />
        ) : (
          <UserCircle size={size * 0.5} weight="thin" className="text-[#a1a1aa]" />
        )}
        {uploading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Spinner size={20} className="animate-spin text-[#002FA7]" />
          </div>
        )}
      </div>

      {!disabled && (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            data-testid={`${testId}-upload-btn`}
            className="inline-flex items-center gap-1.5 border border-[#e4e4e7] bg-white px-3 py-1.5 text-xs font-semibold text-[#09090b] hover:bg-[#f4f4f5] disabled:opacity-50"
          >
            <Camera size={14} weight="bold" /> {src ? "Change" : "Upload"} photo
          </button>
          {src && (
            <button
              type="button"
              onClick={clearPhoto}
              disabled={uploading}
              data-testid={`${testId}-clear-btn`}
              className="inline-flex items-center gap-1.5 text-xs text-[#52525b] hover:text-[#FF2A2A] px-3 py-1"
            >
              <Trash size={12} weight="bold" /> Remove
            </button>
          )}
          <div className="text-[10px] text-[#a1a1aa]">JPG / PNG / WEBP · max 5 MB</div>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

/** Compact thumbnail for use in list tables (read-only). */
export function PhotoThumb({ value, size = 32, fallback = null, testId }) {
  if (!value) {
    return fallback || (
      <div
        className="bg-[#f4f4f5] border border-[#e4e4e7] flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <UserCircle size={size * 0.6} weight="thin" className="text-[#a1a1aa]" />
      </div>
    );
  }
  const base = API_BASE.replace(/\/api$/, "");
  const src = value.startsWith("http") ? value : `${base}${value}`;
  return (
    <img
      src={src}
      alt="photo"
      className="object-cover border border-[#e4e4e7]"
      style={{ width: size, height: size }}
      data-testid={testId}
    />
  );
}
